import ExcelJS, {
  ConditionalFormattingRule,
  ImageHyperlinkValue,
  ImagePosition,
  ImageRange,
  Style,
} from "exceljs";
import { saveAs } from "file-saver";
import _ from "lodash";
import FileUtils from "../../utils/FileUtils";
import {
  ExportTableColumn,
  ExportTreeData,
  TableExcelExportOptions,
} from "./export.model";

//convert row/column index to excel column/row
// const getCell = (column: number, row: number) => {
//   let s = "";
//   while (column >= 0) {
//     s = String.fromCharCode((column % 26) + 65) + s;
//     column = Math.floor(column / 26) - 1;
//   }
//   return s + row.toString();
// };
export const exportTableWorker = async (
  filename: string,
  columns: ExportTableColumn[],
  data: any[],
  exportAs: "json" | "xlsx" | "csv" = "xlsx",
  otherOptions?: TableExcelExportOptions
) => {
  if (exportAs === "json") {
    const result = [];
    for (const e of data) {
      const obj = {};
      for (const column of columns) {
        const value = column.selector(e);
        if (typeof value === "function") {
          obj[column.id] = await value;
        } else {
          obj[column.id] = value;
        }
      }
      result.push(obj);
    }

    // const result = data.map((e) => {
    //   const obj = {};
    //   columns.forEach((c) => (obj[c.id] = c.selector(e)));
    //   return obj;
    // });
    var blob = new Blob([JSON.stringify(result)], {
      type: "application/json",
    });
    saveAs(
      blob,
      filename.endsWith(`.${exportAs}`) ? filename : filename + `.${exportAs}`
    );
  } else {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "iberio";

    workbook.created = new Date();
    workbook.modified = new Date();

    // check these
    workbook.properties.date1904 = true;
    workbook.calcProperties.fullCalcOnLoad = true;

    const sheet = workbook.addWorksheet("Data");
    let totalsRowExist = false;
    columns.forEach((column, index) => {
      if (column.totalFunction) {
        totalsRowExist = true;
      }
    });

    sheet.columns = columns.map((e) => ({
      width: e.width,
      style: _.merge(
        {},
        {
          alignment: {
            vertical: "middle",
          },
        } as Partial<ExcelJS.Style>,
        e.style
      ),
    }));

    const columnsForTable = columns.map((e) => ({
      // header: e.label,
      // key: e.id,
      name: e.label,
      totalsRowFunction: e.totalFunction,
      filterButton: e.type !== "image",
      totalsRowLabel: "",
    }));

    let indexRow = 1;
    const imagesToAdd: {
      imageId: number;
      range:
        | string
        | ({ editAs?: string } & ImageRange & {
              hyperlinks?: ImageHyperlinkValue;
            })
        | ({ editAs?: string } & ImagePosition & {
              hyperlinks?: ImageHyperlinkValue;
            });
      rowIndex: number;
      columnIndex: number;
      height: number;
    }[] = [];
    const rowData = await Promise.all(
      data.map(async (e, index) => {
        let indexColumn = index + 1;
        const rowEntry = [];
        for (const column of columns) {
          const valueFC = column.selector(e);
          let value = null;
          if (
            typeof valueFC === "object" &&
            typeof valueFC?.then === "function"
          ) {
            value = await valueFC;
          } else {
            value = valueFC;
          }

          if (column.type === "image") {
            const cell = sheet.getCell(indexRow + 1, indexColumn);
            cell.style = {
              alignment: {
                wrapText: true,
              },
            };
            const imageBase64 = value;
            if (
              imageBase64 &&
              ["jpeg", "png", "gif"].includes(
                FileUtils.extractMimetypeOfBase64(imageBase64)
              )
            ) {
              const id = workbook.addImage({
                base64: imageBase64,
                extension: FileUtils.extractMimetypeOfBase64(imageBase64) as
                  | "jpeg"
                  | "png"
                  | "gif",
              });

              imagesToAdd.push({
                imageId: id,
                // range: {
                //   tl: { col: indexColumn, row: indexRow },
                //   br: { col: indexColumn + 0.99, row: indexRow + 0.99 },

                //   // editAs: "undefined",
                // } as any,
                range: `${String.fromCharCode(64 + indexColumn)}${
                  indexRow + 1
                }:${String.fromCharCode(64 + indexColumn)}${indexRow + 1}`,
                rowIndex: indexRow,
                columnIndex: indexColumn,
                height: column.height,
              });
              value = Array(Math.floor(column.height / 25))
                .fill(1)
                .map(() => "-\n\r")
                .join("");
            } else {
              value = "";
            }
          }
          rowEntry.push(value);
          indexColumn++;
        }

        return rowEntry;
      })
    );
    // for (const e of data) {
    //   let indexColumn = 1;
    //   // if (height) {
    //   //   row.height = height;
    //   // }
    //   rowData.push(rowEntry);
    //   indexRow++;
    // }

    const table = sheet.addTable({
      columns: columnsForTable,
      name: "table",
      ref: "A1",
      rows: rowData,
      headerRow: true,
      totalsRow: totalsRowExist,
      style: {},
    });

    columns.forEach((column, index) => {
      if (column.conditionalFormats) {
        sheet.addConditionalFormatting({
          ref: `${String.fromCharCode(64 + index)}2:${String.fromCharCode(
            64 + index
          )}${data.length + 1}`,
          rules: column.conditionalFormats,
        });
      }
    });

    if (otherOptions?.autoFilter) {
      sheet.autoFilter = {
        from: {
          row: 1,
          column: 1,
        },
        to: {
          row: data.length,
          column: columns.length,
        },
      };
    }
    if (otherOptions?.frozenHeader) {
      sheet.views = [
        {
          state: "frozen",
          ySplit: 1,
          xSplit: 0,
        },
      ];
    }
    if (imagesToAdd) {
      imagesToAdd.forEach((image) => {
        // sheet.getCell( image.rowIndex + 1, image.columnIndex + 1).
        sheet.addImage(image.imageId, image.range);
      });
    }

    const buffer =
      exportAs === "xlsx"
        ? await workbook.xlsx.writeBuffer()
        : await workbook.csv.writeBuffer();

    var blob = new Blob([buffer], {
      type:
        exportAs === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "text/csv;charset=utf-8",
    });
    saveAs(
      blob,
      filename.endsWith(`.${exportAs}`) ? filename : filename + `.${exportAs}`
    );
  }
};

export const exportTreeDataWorker = async (
  filename: string,
  dataColumns: ExportTableColumn[],
  data: ExportTreeData[]
) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "iberio";

  workbook.created = new Date();
  workbook.modified = new Date();

  // check these
  workbook.properties.date1904 = true;
  workbook.calcProperties.fullCalcOnLoad = true;

  data.forEach((unit) => {
    const sheet = workbook.addWorksheet(unit.name);

    sheet.columns = [
      {
        width: 60,
        style: {
          alignment: {
            vertical: "middle",
          },
        },
      },
      ...dataColumns.map((e) => ({
        width: e.width,
        style: _.merge(
          {},
          {
            alignment: {
              vertical: "middle",
            },
          } as Partial<ExcelJS.Style>,
          e.style
        ),
      })),
    ];
    const row = sheet.addRow(["", ...dataColumns.map((e) => e.label)]);
    row.font = {
      bold: true,
    };

    const unitRow = sheet.addRow([
      unit.name,
      ...dataColumns.map((e) => e.selector(unit.data)),
    ]);
    unitRow.eachCell((cell, colNumber) => {
      if (colNumber <= dataColumns.length + 1) {
        cell.border = {
          top: { style: "medium" },
          bottom: { style: "medium" },
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: {
            argb: "FFADD8E6",
          },
        };
      }
    });

    unit.entities.forEach((entity) => {
      const entityRow = sheet.addRow([
        entity.name,
        ...dataColumns.map((e) => e.selector(entity.data)),
      ]);
      entityRow.eachCell((cell, colNumber) => {
        if (colNumber <= dataColumns.length + 1) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: {
              argb: "FFEEEEEE",
            },
          };
          cell.border = {
            top: { style: "thin" },
            bottom: { style: "thin" },
          };
        }
      });

      entity.objects.forEach((object) => {
        const objectRow = sheet.addRow([
          object.name,
          ...dataColumns.map((e) => e.selector(object.data)),
        ]);
        objectRow.font = {
          bold: true,
        };

        object.entries.forEach((entry) => {
          const entryRow = sheet.addRow([
            entry.name,
            ...dataColumns.map((e) => e.selector(entry.data)),
          ]);
        });
      });
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();

  var blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, filename.endsWith(`.xlsx`) ? filename : filename + `.xlsx`);
};

export interface ExcelExportColumnConfig<TData> {
  key: string;
  header: string; // Label for the column header
  width?: number; // Optional column width
  renderValue: (value: TData) => any; // Function to render cell value
  renderFooter?: (data: TData[]) => any; // Function to render footer value
  style?: Partial<Style>; // Optional styling for cells

  conditionalFormats?: ConditionalFormattingRule[];
  totalFunction?:
    | "average"
    | "sum"
    | "countNums"
    | "count"
    | "max"
    | "min"
    | "stdDev"
    | "var";
}

export interface CategorizedData<TCategory, TRow> {
  category: TCategory; // Object containing the category data
  rows: TRow[]; // Array of sub-rows within the category
}

export async function exportCategorizedData<TCategory, TRow>(
  fileName: string = "ExportedData.xlsx",
  categorizedData: CategorizedData<TCategory, TRow>[],
  categoryColumns: ExcelExportColumnConfig<TCategory>[],
  subRowColumns: ExcelExportColumnConfig<TRow>[]
): Promise<void> {
  try {
    const ExcelJS = await import("exceljs"); // Lazy-loaded for performance
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "iberio";

    workbook.created = new Date();
    workbook.modified = new Date();

    // check these
    workbook.properties.date1904 = true;
    workbook.calcProperties.fullCalcOnLoad = true;

    const sheet = workbook.addWorksheet("Data");

    const allColumns = [...categoryColumns, ...subRowColumns];

    sheet.columns = allColumns.map((e) => ({
      width: e.width,
      style: _.merge(
        {},
        {
          alignment: {
            vertical: "middle",
          },
        } as Partial<ExcelJS.Style>,
        e.style
      ),
    }));

    const rows: any[][] = [];
    // Process and style rows
    const addRow = (rowData: any[]) => {
      rows.push(rowData);
      return rowData;
    };

    // Process each category and its rows
    categorizedData.forEach(({ category, rows }) => {
      // Add the category row
      const categoryRowData = categoryColumns.map((col) =>
        col.renderValue(category)
      );
      addRow([...categoryRowData, ...new Array(subRowColumns.length).fill("")]);

      // Add sub-rows
      rows.forEach((row) => {
        const subRowData = subRowColumns.map((col) => col.renderValue(row));
        addRow([...new Array(categoryColumns.length).fill(""), ...subRowData]);
      });
    });

    sheet.addTable({
      name: "MyTable",
      ref: "A1",
      headerRow: true,
      totalsRow: true,
      style: {
        theme: "TableStyleLight9",
        showRowStripes: true,
      },
      columns: allColumns.map((col) => ({
        name: col.header || col.key,
        // filterButton: true,
        totalsRowFunction: col.totalFunction,
      })),
      rows: rows,
    });

    allColumns.forEach((column, index) => {
      if (column.conditionalFormats) {
        sheet.addConditionalFormatting({
          ref: `${String.fromCharCode(64 + index)}2:${String.fromCharCode(
            64 + index
          )}${rows.length + 1}`,
          rules: column.conditionalFormats,
        });
      }
    });
    // Export the workbook as an Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, fileName);
  } catch (error) {
    console.error("Error exporting categorized data:", error);
  }
}
export const readExcelFileWorker = (file: File) => {
  return new Promise<any[]>((resolve, reject) => {
    try {
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async (e) => {
        const buffer = e.target.result;

        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer as ExcelJS.Buffer);

        const worksheet = workbook.getWorksheet(1); // first sheet

        // read header row, the column headers are the variable names for the entries.
        const headerRow = worksheet.getRow(1);
        const header = headerRow.values as string[];

        const rows = [];

        let headers = [];

        worksheet.eachRow((row, rowNumber) => {
          const rowValues = (row.values as ExcelJS.CellValue[]).slice(1); // ExcelJS startet bei Index 1

          if (rowNumber === 1) {
            headers = rowValues;
          } else {
            const obj = {};
            headers.forEach((header, index) => {
              const cell = rowValues[index];
              obj[header] = extractValue(cell);
            });
            rows.push(obj);
          }
        });

        resolve(rows);
      };

      reader.readAsArrayBuffer(file);
    } catch (err) {
      reject(err);
    }
  });
};
function extractValue(cell) {
  if (cell == null) return null;

  if (typeof cell === "object") {
    if ("result" in cell) return extractValue(cell.result); // Formel mit Datum
    if (cell instanceof Date) return cell.toISOString(); // ISO-Format z.B. "2025-04-09T00:00:00.000Z"
    if ("text" in cell) return cell.text;
    if ("richText" in cell)
      return cell.richText.map((part) => part.text).join("");
    if ("value" in cell && cell.value instanceof Date)
      return cell.value.toISOString();
    return cell.formula || null;
  }

  if (cell instanceof Date) return cell.toISOString();
  return cell;
}
